/**
 * Engineering Agent dock provider.
 *
 * Lives at the root layout so the chat panel survives route changes —
 * close it on the bookings page, navigate to wifi, the panel is still
 * open with the same thread loaded.
 *
 * State persistence:
 *  - `isOpen`, `agent`, and the last-active threadId for each agent are
 *    persisted to localStorage. A refresh restores exactly where you were.
 *  - Cross-tab sync: a `storage` event listener keeps two tabs in sync
 *    (open on tab A → see it open on tab B after a focus).
 *
 * Push-content layout:
 *  - Children render inside a wrapper that gains `padding-right: 440px`
 *    when the dock is open. The dock itself is `fixed inset-y-0 right-0`
 *    so it doesn't reflow during the slide-in transition; only the main
 *    wrapper's padding animates. End effect = page content slides left.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { cn } from '@/lib/utils'
import { EngineeringAgentDock } from './dock'

export type EngineeringAgentId =
  | 'engineering-bookings'
  | 'engineering-bookings-csv'
  | 'engineering-wifi'
  | 'engineering-crm'
  | 'engineering-marketing'
  | 'engineering-loyalty'
  | 'engineering-reviews'

const ENGINEERING_AGENT_IDS: readonly EngineeringAgentId[] = [
  'engineering-bookings',
  'engineering-bookings-csv',
  'engineering-wifi',
  'engineering-crm',
  'engineering-marketing',
  'engineering-loyalty',
  'engineering-reviews',
]

function isEngineeringAgentId(v: unknown): v is EngineeringAgentId {
  return (
    typeof v === 'string' &&
    (ENGINEERING_AGENT_IDS as readonly string[]).includes(v)
  )
}

/**
 * Scope handed to the agent when opened from a feature page — the same idea
 * as the venue context the dock already derives from the URL, but explicit so
 * a loyalty scheme page can pass its scheme id, a venue reviews page its
 * serial, etc. Merged with URL-derived venue context inside the dock.
 */
export interface EngineeringAgentScope {
  schemeId?: string | null
  schemeName?: string | null
  serial?: string | null
  venueName?: string | null
  /** CSV import job id — lets the bookings-csv agent know the exact upload. */
  importJobId?: string | null
}

const STORAGE_PREFIX = 'engineering-agent'
const KEY_OPEN = `${STORAGE_PREFIX}.open`
const KEY_AGENT = `${STORAGE_PREFIX}.agent`
const KEY_THREAD = (agent: EngineeringAgentId) =>
  `${STORAGE_PREFIX}.thread.${agent}`

export const DOCK_WIDTH_PX = 440

interface EngineeringAgentState {
  isOpen: boolean
  agent: EngineeringAgentId
  threadId: string | null
  /** Explicit scope from the entry point (scheme id, serial, …). Ephemeral —
   * not persisted; set per `open()` and cleared on agent switch. */
  scope: EngineeringAgentScope | null
}

interface EngineeringAgentContextValue extends EngineeringAgentState {
  open: (
    agent?: EngineeringAgentId,
    scope?: EngineeringAgentScope | null
  ) => void
  close: () => void
  toggle: (
    agent?: EngineeringAgentId,
    scope?: EngineeringAgentScope | null
  ) => void
  setAgent: (agent: EngineeringAgentId) => void
  /** Used by the dock when the server confirms a (new) threadId via SSE. */
  setThreadId: (threadId: string | null) => void
}

const Ctx = createContext<EngineeringAgentContextValue | null>(null)

export function useEngineeringAgent(): EngineeringAgentContextValue {
  const v = useContext(Ctx)
  if (!v) {
    throw new Error(
      'useEngineeringAgent must be used inside <EngineeringAgentProvider>'
    )
  }
  return v
}

export function useEngineeringAgentIfEnabled(): EngineeringAgentContextValue | null {
  return useContext(Ctx)
}

// ── localStorage helpers ────────────────────────────────────────────────────

function readBool(key: string, fallback: boolean): boolean {
  if (typeof window === 'undefined') return fallback
  const v = window.localStorage.getItem(key)
  if (v === null) return fallback
  return v === '1' || v === 'true'
}

function writeBool(key: string, value: boolean): void {
  try {
    window.localStorage.setItem(key, value ? '1' : '0')
  } catch {
    /* quota / private mode — non-fatal */
  }
}

function readAgent(fallback: EngineeringAgentId): EngineeringAgentId {
  if (typeof window === 'undefined') return fallback
  const v = window.localStorage.getItem(KEY_AGENT)
  return isEngineeringAgentId(v) ? v : fallback
}

function writeString(key: string, value: string | null): void {
  try {
    if (value === null) window.localStorage.removeItem(key)
    else window.localStorage.setItem(key, value)
  } catch {
    /* non-fatal */
  }
}

// ── Provider ────────────────────────────────────────────────────────────────

interface EngineeringAgentProviderProps {
  children: ReactNode
  /** Whether the current user is allowed to see the dock. Pass `false`
   *  for non-@stampede.ai users — the wrapper just renders children
   *  untouched, no localStorage reads, no panel. */
  enabled: boolean
}

export function EngineeringAgentProvider({
  children,
  enabled,
}: EngineeringAgentProviderProps) {
  // Always read localStorage on init — gating these on `enabled` was a bug.
  // `useUser()` resolves asynchronously, so the provider first renders with
  // `enabled=false` and the useState initializers ran once with that value;
  // when `enabled` flipped true the persist effects then wrote the (stale)
  // `false`/default state back to localStorage, wiping the user's actual
  // preference. Reading localStorage unconditionally is harmless when
  // disabled — the dock UI is gated below on `enabled` anyway.
  const [isOpen, setIsOpen] = useState<boolean>(() => readBool(KEY_OPEN, false))
  const [agent, setAgentState] = useState<EngineeringAgentId>(() =>
    readAgent('engineering-bookings')
  )
  const [threadId, setThreadIdState] = useState<string | null>(() => {
    if (typeof window === 'undefined') return null
    const initialAgent = readAgent('engineering-bookings')
    return window.localStorage.getItem(KEY_THREAD(initialAgent))
  })
  const [scope, setScope] = useState<EngineeringAgentScope | null>(null)

  // Track latest values in a ref so the storage-event listener can read
  // them without re-binding every render.
  const stateRef = useRef({ isOpen, agent, threadId })
  stateRef.current = { isOpen, agent, threadId }

  // Persist on change
  useEffect(() => {
    if (!enabled) return
    writeBool(KEY_OPEN, isOpen)
  }, [enabled, isOpen])

  useEffect(() => {
    if (!enabled) return
    writeString(KEY_AGENT, agent)
    // When agent changes, restore that agent's last thread (if any).
    const stored = window.localStorage.getItem(KEY_THREAD(agent))
    setThreadIdState(stored)
  }, [enabled, agent])

  useEffect(() => {
    if (!enabled) return
    writeString(KEY_THREAD(agent), threadId)
  }, [enabled, agent, threadId])

  // Cross-tab sync
  useEffect(() => {
    if (!enabled) return
    function onStorage(e: StorageEvent) {
      if (!e.key?.startsWith(STORAGE_PREFIX)) return
      if (e.key === KEY_OPEN) {
        setIsOpen(e.newValue === '1' || e.newValue === 'true')
      } else if (e.key === KEY_AGENT) {
        if (isEngineeringAgentId(e.newValue)) {
          setAgentState(e.newValue)
        }
      } else if (e.key === KEY_THREAD(stateRef.current.agent)) {
        setThreadIdState(e.newValue)
      }
    }
    window.addEventListener('storage', onStorage)
    return () => {
      window.removeEventListener('storage', onStorage)
    }
  }, [enabled])

  // Actions
  const open = useCallback(
    (
      nextAgent?: EngineeringAgentId,
      nextScope?: EngineeringAgentScope | null
    ) => {
      if (nextAgent) setAgentState(nextAgent)
      // Scope is set whenever open() is called with one (incl. null to clear).
      if (nextScope !== undefined) setScope(nextScope)
      setIsOpen(true)
    },
    []
  )

  const close = useCallback(() => {
    setIsOpen(false)
  }, [])

  const toggle = useCallback(
    (
      nextAgent?: EngineeringAgentId,
      nextScope?: EngineeringAgentScope | null
    ) => {
      if (nextAgent) setAgentState(nextAgent)
      if (nextScope !== undefined) setScope(nextScope)
      setIsOpen((v) => !v)
    },
    []
  )

  const setAgent = useCallback((next: EngineeringAgentId) => {
    setAgentState(next)
    // Switching agents in the dock drops any page-scoped context — the new
    // agent isn't necessarily about the same scheme/venue.
    setScope(null)
  }, [])

  const setThreadId = useCallback((next: string | null) => {
    setThreadIdState(next)
  }, [])

  const value = useMemo<EngineeringAgentContextValue>(
    () => ({
      isOpen,
      agent,
      threadId,
      scope,
      open,
      close,
      toggle,
      setAgent,
      setThreadId,
    }),
    [isOpen, agent, threadId, scope, open, close, toggle, setAgent, setThreadId]
  )

  // ALWAYS wrap children in the same DOM structure regardless of `enabled`.
  // Previously this had an early-return that returned `<>{children}</>`
  // when disabled and a Provider+wrapper when enabled — every flip of
  // `enabled` (e.g. when useUser() finally resolves) remounted the entire
  // app subtree, including the dock. That's why the panel kept "closing
  // out" — it was being unmounted and re-mounted from scratch. Keeping
  // the tree stable means useUser() loading is invisible to the dock.
  //
  // The dock itself + the right-padding only apply when `enabled` is
  // true; non-stampede users see no padding and no dock UI.
  const dockVisible = enabled && isOpen

  return (
    <Ctx.Provider value={value}>
      <div
        className={cn(
          'min-h-screen transition-[padding-right] duration-300 ease-out',
          dockVisible ? `pr-[${DOCK_WIDTH_PX}px]` : 'pr-0'
        )}
        // Inline width: Tailwind JIT doesn't always pick up `pr-[440px]`
        // reliably across all build configs in this monorepo, so we also
        // set it inline. Inline wins, transition still applies.
        style={{ paddingRight: dockVisible ? `${DOCK_WIDTH_PX}px` : 0 }}
      >
        {children}
      </div>
      {enabled ? <EngineeringAgentDock /> : null}
    </Ctx.Provider>
  )
}
